import { useRef, useState, useContext } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Formik components
import { Formik, Form } from "formik";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import SoftButton from "components/SoftButton";
import { addErrorLog } from "../../../../../Logger/logger";

import * as Yup from "yup";

// import validations from "layouts/pages/roles-and-permissions/schemas/validations";
import form from "layouts/pages/roles-and-permissions/schemas/form";
import initialValues from "layouts/pages/roles-and-permissions/schemas/initialValues";
import FormField from "layouts/pages/roles-and-permissions/components/FormField";

// import { insertQuery } from "helpers/Graphql";
import { updateQuery } from "api/UdasWrapper";

import { useAuth0 } from "@auth0/auth0-react";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DiscardDialog from "cxfcomponents/DiscardDialog";
import { useDispatch } from "react-redux";
import { useRoles } from "hooks/useRoles";
import { setRoles } from "redux/actions/portal/rolesActionCreator";
import { PREDEFINED_ROLES } from "constants/portal";
import { UserContext } from "context/AppProvider/UserContext";
import { createRole } from "api/RolesApi";
import DeletePropertiesSection from "cxfcomponents/DeletePropertiesSection";

function RoleForm({
	open = false,
	onClose = () => {},
	mode = "",
	selectedRole,
	setSelectedRole,
	onRoleUpdated,
	canEdit = true,
	canDelete = false,
	onDelete
}) {
	const { userData } = useContext(UserContext);
	const { roles } = useRoles(false);
	const [openDiscardDialog, setOpenDiscardDialog] = useState(false);
	const formRef = useRef(null);
	const { user } = useAuth0();
	const {
		formId,
		formField: { roleName, userType, description, status }
	} = form;
	const validations = Yup.object().shape({
		[roleName.name]: Yup.string().required(roleName.errorMsg),
		[userType.name]: Yup.string()
			.oneOf(
				userType.options.map((option) => option.value),
				userType.errorMsg
			)
			.required(userType.errorMsg),
		[description.name]: Yup.string(),
		[status.name]: Yup.string()
			.oneOf(
				status.options.map((option) => option.value),
				userType.errorMsg
			)
			.required(status.errorMsg)
	});
	const dispatch = useDispatch();

	const handleOpenDiscardDialog = () => {
		setOpenDiscardDialog(true);
	};

	const handleDiscardDialogClose = () => {
		setOpenDiscardDialog(false);
	};

	const checkDirtyAndClose = () => {
		if (formRef?.current?.dirty) {
			handleOpenDiscardDialog();
			return;
		}
		closeForm();
	};

	const closeForm = () => {
		formRef?.current?.resetForm();
		onClose();
	};

	const handleRoleSubmit = async (values, actions) => {
		if (mode === "edit" && !canEdit) {
			actions.setSubmitting(false);
			return;
		}
		if (mode === "add") {
			try {
				const input = {
					created_by: ``,
					description: `${values.description}`,
					name: `${values.name}`,
					notes: ``,
					status: `${values.status}`,
					user_type: `${values.user_type}`,
					created_by_role_id: `${userData?.role_id}`
				};

				const result = await createRole(user.stripeId, input);

				const newRole = result?.resultObject
					? Object.assign(result.resultObject, input)
					: {};

				toast.success("Role added successfully!");

				onClose();
				dispatch(setRoles([...roles, newRole]));
				setSelectedRole?.(newRole);
			} catch (error) {
				addErrorLog("mutation CreateRoles", error, user?.stripeId);
			}

			return;
		} else {
			try {
				const data = JSON.stringify({
					query: `mutation UpdateRoles($tenant_id: String!, $updateRolesId: String!, $input: RolesInput) {
          updateRoles(tenant_id: $tenant_id, id: $updateRolesId, input: $input) {
            isSuccessful
            message
            resultObject
          }
        }`,
					variables: {
						input: {
							created_by: ``,
							description: `${values.description}`,
							name: `${values.name}`,
							notes: ``,
							status: `${values.status}`,
							user_type: `${values.user_type}`
						},
						updateRolesId: `${selectedRole.id}`,
						tenant_id: `${user.stripeId}`
					}
				});
				if ((await updateQuery(data))?.updateRoles?.isSuccessful) {
					toast.success("Role updated successfully!");

					const updatedRoleData = {
						name: values.name,
						user_type: values.user_type,
						description: values.description,
						status: values.status
					};
					onClose();
					dispatch(
						setRoles(
							roles.map((role) =>
								role.id === selectedRole.id
									? {
											...role,
											...updatedRoleData
										}
									: role
							)
						)
					);
					setSelectedRole?.((selectedRole) => ({
						...selectedRole,
						...updatedRoleData
					}));
					onRoleUpdated?.();
				} else toast.error("Unable to update role");
			} catch (error) {
				addErrorLog("mutation UpdateRoles", error, user?.stripeId);
			}
		}
		actions.setSubmitting(false);
		actions.resetForm();
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		formRef?.current?.submitForm();
	};

	const validateRoleName = (value) => {
		let error;

		if (!value) {
			return "Role name is required.";
		}

		if (PREDEFINED_ROLES.includes(value.toLowerCase())) {
			return "Role name is already exists.";
		}

		return error;
	};

	return (
		<>
			<Dialog
				open={open}
				fullWidth={true}
				maxWidth="sm"
				sx={{ "& .MuiPaper-root": { overflow: "visible" } }}
				data-testid="role-form"
			>
				<DialogTitle data-testid="form-title">
					{mode === "add" ? "Add New Role" : "Role Properties"}
				</DialogTitle>
				<DialogContent
					dividers
					sx={{ "&.MuiDialogContent-root": { overflow: "visible" } }}
				>
					<Formik
						initialValues={mode === "edit" ? selectedRole : initialValues}
						validationSchema={validations}
						onSubmit={handleRoleSubmit}
						innerRef={formRef}
					>
						{({ values, errors, touched }) => {
							return (
								<Form
									id={formId}
									autoComplete="off"
									style={canEdit ? undefined : { pointerEvents: "none", opacity: 0.7 }}
								>
									<FormField
										type={roleName.type}
										label={roleName.label}
										name={roleName.name}
										validate={validateRoleName}
										// success={!errors[roleName.name] && touched[roleName.name]}
										error={
											errors[roleName.name] && touched[roleName.name]
										}
										placeholder={
											roleName.placeholder
												? roleName.placeholder
												: ""
										}
										forWhat="Add Role"
										required
									/>
									{/* <FormField
                    label={userType.label}
                    name={userType.name}
                    defaultValue={{
                      value: values[userType.name],
                      label: values[userType.name]
                        ? values[userType.name].charAt(0).toUpperCase() +
                          values[userType.name].slice(1)
                        : "Select...",
                    }}
                    // success={!errors[userType.name]}
                    error={Boolean(errors[userType.name])}
                    placeholder={userType.placeholder ? userType.placeholder : ""}
                    options={userType.options}
                  /> */}
									<FormField
										type={description.type}
										label={description.label}
										name={description.name}
										// success={!errors[description.name] && touched[description.name]}
										error={
											errors[description.name] &&
											touched[description.name]
										}
										placeholder={
											description.placeholder
												? description.placeholder
												: ""
										}
										forWhat="Add Role"
									/>
									<FormField
										label={status.label}
										name={status.name}
										defaultValue={{
											value: values[status.name],
											label: values[status.name]
												? values[status.name]
														.charAt(0)
														.toUpperCase() +
													values[status.name].slice(1)
												: "Select..."
										}}
										// success={!errors[status.name]}
										error={Boolean(errors[status.name])}
										placeholder={
											status.placeholder ? status.placeholder : ""
										}
										s
										options={status.options}
									/>
								</Form>
							);
						}}
					</Formik>
					{mode === "edit" && canDelete && onDelete && (
						<DeletePropertiesSection open={open} resourceName="role" onDelete={onDelete} />
					)}
				</DialogContent>
				<DialogActions>
					<SoftButton
						variant="outlined"
						color="info"
						onClick={checkDirtyAndClose}
					>
						Cancel
					</SoftButton>
					{canEdit && (
						<SoftButton variant="contained" color="info" onClick={handleSubmit}>
							{mode} Role
						</SoftButton>
					)}
				</DialogActions>
			</Dialog>
			<DiscardDialog
				open={openDiscardDialog}
				handleDialogClose={handleDiscardDialogClose}
				onAccept={closeForm}
			/>
		</>
	);
}

RoleForm.propTypes = {
	open: PropTypes.bool,
	onClose: PropTypes.func,
	onRoleUpdated: PropTypes.func,
	onDelete: PropTypes.func,
	canEdit: PropTypes.bool,
	canDelete: PropTypes.bool,
	setSelectedRole: PropTypes.func,
	mode: PropTypes.string,
	selectedRole: PropTypes.shape({
		id: PropTypes.string,
		name: PropTypes.string,
		userType: PropTypes.string,
		description: PropTypes.string,
		people: PropTypes.arrayOf(PropTypes.object),
		status: PropTypes.string
	})
};

export default RoleForm;
